// Creative Work Footprint Survey Questions
// 30 questions total: 5 questions per force (S, A, C, G, F, P)

import SparkIcon from '../icons/Spark.svg';
import AnchorIcon from '../icons/Anchor.svg';
import ConnectorIcon from '../icons/Connector.svg';
import DeciderIcon from '../icons/Decider.svg';
import BuilderIcon from '../icons/Builder.svg';
import PulseIcon from '../icons/Pulse.svg';

export const surveyQuestions = [
  // THE SPARK - Think It (Questions 1-5)
  {
    id: 1,
    force: 's',
    question: 'I often have multiple ideas happening at once'
  },
  {
    id: 2,
    force: 's',
    question: 'I\'m energized by exploring new possibilities'
  },
  {
    id: 3,
    force: 's',
    question: 'I like to brainstorm and imagine "what if" scenarios'
  },
  {
    id: 4,
    force: 's',
    question: 'Others come to me for creative or innovative thinking'
  },
  {
    id: 5,
    force: 's',
    question: 'I generate ideas across different domains easily'
  },

  // THE ANCHOR - Note It (Questions 6-10)
  {
    id: 6,
    force: 'a',
    question: 'I like to organize information and ideas systematically'
  },
  {
    id: 7,
    force: 'a',
    question: 'I\'m comfortable documenting processes and insights'
  },
  {
    id: 8,
    force: 'a',
    question: 'I prefer clarity and structure in my work'
  },
  {
    id: 9,
    force: 'a',
    question: 'I enjoy creating systems that others can follow'
  },
  {
    id: 10,
    force: 'a',
    question: 'I naturally keep track of details and follow through'
  },

  // THE CONNECTOR - Share It (Questions 11-15)
  {
    id: 11,
    force: 'c',
    question: 'I enjoy bringing people together around ideas'
  },
  {
    id: 12,
    force: 'c',
    question: 'I\'m skilled at communicating complex ideas simply'
  },
  {
    id: 13,
    force: 'c',
    question: 'I like building relationships and networks'
  },
  {
    id: 14,
    force: 'c',
    question: 'I get energized by collaboration'
  },
  {
    id: 15,
    force: 'c',
    question: 'I help others understand and align with goals'
  },

  // THE DECIDER - Decide It (Questions 16-20)
  {
    id: 16,
    force: 'g',
    question: 'I\'m comfortable making tough prioritization calls'
  },
  {
    id: 17,
    force: 'g',
    question: 'I evaluate ideas against clear criteria'
  },
  {
    id: 18,
    force: 'g',
    question: 'I can say "no" when something doesn\'t align with priorities'
  },
  {
    id: 19,
    force: 'g',
    question: 'I like taking responsibility for decisions'
  },
  {
    id: 20,
    force: 'g',
    question: 'I\'m good at weighing trade-offs and choosing direction'
  },

  // THE BUILDER - Do It (Questions 21-25)
  {
    id: 21,
    force: 'f',
    question: 'I like the execution and building phase most'
  },
  {
    id: 22,
    force: 'f',
    question: 'I\'m energized by getting things done'
  },
  {
    id: 23,
    force: 'f',
    question: 'I naturally move ideas toward completion'
  },
  {
    id: 24,
    force: 'f',
    question: 'I\'m comfortable with hands-on work'
  },
  {
    id: 25,
    force: 'f',
    question: 'I can work through obstacles to deliver results'
  },

  // THE PULSE - Remind It (Questions 26-30)
  {
    id: 26,
    force: 'p',
    question: 'I\'m good at follow-up and keeping momentum'
  },
  {
    id: 27,
    force: 'p',
    question: 'I naturally track deadlines and dependencies'
  },
  {
    id: 28,
    force: 'p',
    question: 'I like keeping projects on schedule'
  },
  {
    id: 29,
    force: 'p',
    question: 'I\'m reliable in seeing things through to completion'
  },
  {
    id: 30,
    force: 'p',
    question: 'I notice when things are falling off track and step in'
  }
];

export const forceInfo = {
  s: {
    name: 'The Spark',
    color: '#FF6B6B',
    icon: SparkIcon,
    description: 'Think It - You ignite possibilities and generate creative concepts',
    fullDescription: 'The Spark brings creative energy and imagination to every project. People with high Spark scores generate ideas, explore possibilities, and inspire others with innovative thinking.'
  },
  a: {
    name: 'The Anchor',
    color: '#4ECDC4',
    icon: AnchorIcon,
    description: 'Note It - You secure and organize ideas',
    fullDescription: 'The Anchor brings stability and structure to creative work. People with high Anchor scores organize information, document processes, and create systems that others can follow.'
  },
  c: {
    name: 'The Connector',
    color: '#FFE66D',
    icon: ConnectorIcon,
    description: 'Share It - You align people and ideas',
    fullDescription: 'The Connector brings people together around ideas. People with high Connector scores build relationships, communicate effectively, and help others align on goals.'
  },
  g: {
    name: 'The Decider',
    color: '#95E1D3',
    icon: DeciderIcon,
    description: 'Decide It - You filter and prioritize what moves forward',
    fullDescription: 'The Decider brings decisive filtering to ideas. People with high Gatekeeper scores make tough calls, evaluate against criteria, and determine what moves forward.'
  },
  f: {
    name: 'The Builder',
    color: '#F38181',
    icon: BuilderIcon,
    description: 'Do It - You shape ideas into tangible outcomes',
    fullDescription: 'The Builder brings energy to execution. People with high Forge scores deliver results, move ideas toward completion, and tackle obstacles head-on.'
  },
  p: {
    name: 'The Pulse',
    color: '#AA96DA',
    icon: PulseIcon,
    description: 'Remind It - You keep timing, follow-ups, and priorities alive',
    fullDescription: 'The Pulse brings rhythm and reliability to projects. People with high Pulse scores maintain momentum, track deadlines, and ensure nothing falls through the cracks.'
  }
};

export const answerOptions = [
  { value: 1, label: 'Strongly Disagree', emoji: '✗' },
  { value: 2, label: 'Disagree', emoji: '−' },
  { value: 3, label: 'Neutral', emoji: '○' },
  { value: 4, label: 'Agree', emoji: '+' },
  { value: 5, label: 'Strongly Agree', emoji: '✓' }
];

export const interpretations = {
  'The Spark': {
    nickname: 'The Spark',
    emoji: '💡',
    thinking: true,
    description: 'You\'re a natural innovator and idea generator. Your strength lies in seeing possibilities others might miss and igniting creative energy in projects and people. You bring imagination and fresh perspectives to every challenge. To grow, consider complementing your Spark with stronger organizational skills (The Anchor) or better decision-making discipline (The Decider). In team settings, you\'re most effective when your ideas can flow freely and when you have support from detail-oriented teammates.',
    strength: 'Innovation & Imagination',
    growth: 'Organization & Structure',
    teams: 'Brings creative energy; pairs well with Anchors and Deciders'
  },
  'The Anchor': {
    nickname: 'The Anchor',
    emoji: '⚓',
    thinking: true,
    description: 'You\'re the keeper of structure and clarity. Your strength lies in organizing ideas, documenting processes, and creating systems that others can rely on. You bring stability and follow-through to projects. To grow, consider embracing more risk-taking in idea generation (The Spark) or developing stronger execution capabilities (The Builder). In team settings, your organizational skills are invaluable for turning ideas into actionable plans.',
    strength: 'Organization & Clarity',
    growth: 'Creativity & Execution',
    teams: 'Brings structure and documentation; works well with Sparks and Builders'
  },
  'The Connector': {
    nickname: 'The Connector',
    emoji: '🔗',
    thinking: true,
    description: 'You\'re a natural bridge-builder and communicator. Your strength lies in bringing people together, aligning diverse perspectives, and fostering collaboration. You make complex ideas accessible and create spaces where people feel heard. To grow, consider developing stronger decision-making skills (The Decider) or deeper execution focus (The Builder). In team settings, you\'re the glue that holds groups together and ensures everyone is moving in the same direction.',
    strength: 'Communication & Collaboration',
    growth: 'Decision-Making & Execution',
    teams: 'Brings people together; bridges ideation and execution'
  },
  'The Decider': {
    nickname: 'The Decider',
    emoji: '🚪',
    doing: true,
    description: 'You\'re a decisive filter and priority expert. Your strength lies in evaluating ideas against clear criteria and determining what actually moves forward. You bring discipline and focus to creative work. To grow, consider embracing more ideation (The Spark) or developing stronger execution follow-through (The Pulse). In team settings, your decision-making is crucial for preventing scope creep and keeping projects focused on what matters most.',
    strength: 'Decision-Making & Prioritization',
    growth: 'Ideation & Follow-Through',
    teams: 'Brings focus and discipline; essential for filtering and prioritizing'
  },
  'The Builder': {
    nickname: 'The Builder',
    emoji: '🔨',
    doing: true,
    description: 'You\'re an execution powerhouse. Your strength lies in turning ideas into tangible outcomes, tackling obstacles, and delivering results. You bring momentum and resilience to projects. To grow, consider developing stronger planning skills (The Anchor) or deeper strategic filtering (The Decider). In team settings, you\'re the person who gets things done and inspires others through action and delivery.',
    strength: 'Execution & Results',
    growth: 'Planning & Strategy',
    teams: 'Brings momentum and delivery; essential for getting work done'
  },
  'The Pulse': {
    nickname: 'The Pulse',
    emoji: '💓',
    doing: true,
    description: 'You\'re the keeper of momentum and accountability. Your strength lies in maintaining follow-up, tracking deadlines, and ensuring nothing falls through the cracks. You bring reliability and rhythm to projects. To grow, consider developing stronger creative thinking (The Spark) or deeper collaboration skills (The Connector). In team settings, you\'re essential for keeping everyone on track and maintaining the heartbeat of the project.',
    strength: 'Follow-Up & Accountability',
    growth: 'Creativity & Collaboration',
    teams: 'Brings reliability and momentum; essential for keeping projects alive'
  }
};

/**
 * Get parameterized questions for peer assessment
 * Replaces "I" with the person's first name to create third-person perspective
 * @param {string} firstName - The first name of the person being assessed
 * @returns {Array} - Array of questions with personalized text
 */
export const getPeerQuestions = (firstName) => {
  return surveyQuestions.map(q => {
    // Create personalized question text
    let peerQuestion = q.question;
    
    // Replace common "I" patterns with the person's name
    peerQuestion = peerQuestion.replace(/^I often /i, `${firstName} often `);
    peerQuestion = peerQuestion.replace(/^I\'m /i, `${firstName} is `);
    peerQuestion = peerQuestion.replace(/^I like /i, `${firstName} likes `);
    peerQuestion = peerQuestion.replace(/^I enjoy /i, `${firstName} enjoys `);
    peerQuestion = peerQuestion.replace(/^I get /i, `${firstName} gets `);
    peerQuestion = peerQuestion.replace(/^I help /i, `${firstName} helps `);
    peerQuestion = peerQuestion.replace(/^I generate /i, `${firstName} generates `);
    peerQuestion = peerQuestion.replace(/^I prefer /i, `${firstName} prefers `);
    peerQuestion = peerQuestion.replace(/^I naturally /i, `${firstName} naturally `);
    peerQuestion = peerQuestion.replace(/^I can /i, `${firstName} can `);
    peerQuestion = peerQuestion.replace(/^I\'m comfortable /i, `${firstName} is comfortable `);
    peerQuestion = peerQuestion.replace(/^I evaluate /i, `${firstName} evaluates `);
    peerQuestion = peerQuestion.replace(/^I thrive /i, `${firstName} thrives `);
    peerQuestion = peerQuestion.replace(/^I focus /i, `${firstName} focuses `);
    peerQuestion = peerQuestion.replace(/^I take /i, `${firstName} takes `);
    peerQuestion = peerQuestion.replace(/^I ensure /i, `${firstName} ensures `);
    peerQuestion = peerQuestion.replace(/^I follow /i, `${firstName} follows `);
    peerQuestion = peerQuestion.replace(/^I keep /i, `${firstName} keeps `);
    peerQuestion = peerQuestion.replace(/^I stay /i, `${firstName} stays `);
    
    // Handle "Others come to me" pattern
    peerQuestion = peerQuestion.replace(/Others come to me for/, `Others go to ${firstName} for`);
    
    // Handle possessive "my" -> "their"
    peerQuestion = peerQuestion.replace(/\bmy\b/gi, 'their');
    peerQuestion = peerQuestion.replace(/\bI\b/g, firstName);
    
    return {
      ...q,
      question: peerQuestion,
      originalQuestion: q.question
    };
  });
};
